import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  PatientSearchParams,
  SortableField,
  SortOrder,
} from '@/features/patients/types/patients'
import {
  ActionStatus,
  ActionGroup,
} from '@/features/patient-actions/types/patientActions'
import { useDebounce } from '@/shared/hooks/useDebounce'
import {
  SEARCH_DEBOUNCE_DELAY,
  DEFAULT_PAGE_LIMIT,
  DEFAULT_SORT_FIELD,
  DEFAULT_SORT_ORDER,
} from '@/constants/patientConstants'
import dayjs from 'dayjs'

/**
 * URLパラメータを患者一覧のSingle Source of Truthとして管理するhook。
 * テキスト入力のみローカルstate + デバウンスでURLに反映。
 * それ以外（日付・ソート・アクションフィルタ・ページ）は即座にURLを更新。
 */
export const usePatientListUrlParams = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // URL からパラメータを派生
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const visitDate = urlParams.get('visit_date') ?? ''
  const actionStatus =
    (urlParams.get('action_status') as ActionStatus) || undefined
  const actionGroup =
    (urlParams.get('action_group') as ActionGroup) || undefined
  const page = Number(urlParams.get('page')) || 1
  const limit = Number(urlParams.get('limit')) || DEFAULT_PAGE_LIMIT
  const ordering = urlParams.get('ordering') ?? DEFAULT_SORT_FIELD

  // ordering → sortField + sortOrder の派生
  const { sortField, sortOrder } = useMemo(() => {
    const desc = ordering.startsWith('-')
    return {
      sortField: (desc ? ordering.slice(1) : ordering) as SortableField,
      sortOrder: (desc ? 'desc' : 'asc') as SortOrder,
    }
  }, [ordering])

  // テキスト入力のみローカル state（デバウンス用）
  const [inputValues, setInputValues] = useState({
    nameKanji: urlParams.get('name_kanji') ?? '',
    nameKana: urlParams.get('name_kana') ?? '',
    patientCode: urlParams.get('patient_code') ?? '',
  })
  const debouncedInput = useDebounce(inputValues, SEARCH_DEBOUNCE_DELAY)

  // URL 更新ユーティリティ
  const updateUrl = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(location.search)
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.set(key, value)
        } else {
          params.delete(key)
        }
      })
      const newSearch = params.toString()
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname
      const currentPath = location.pathname + location.search
      if (newPath !== currentPath) {
        navigate(newPath)
      }
    },
    [location.search, location.pathname, navigate]
  )

  // デバウンス後にURLへ反映（初回マウント時のスキップ用）
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    updateUrl({
      name_kanji: debouncedInput.nameKanji || undefined,
      name_kana: debouncedInput.nameKana || undefined,
      patient_code: debouncedInput.patientCode || undefined,
      page: undefined, // テキスト変更時はページリセット
    })
  }, [debouncedInput]) // eslint-disable-line react-hooks/exhaustive-deps

  // Setters
  const setNameKanji = useCallback((v: string) => {
    setInputValues((prev) => ({ ...prev, nameKanji: v }))
  }, [])

  const setNameKana = useCallback((v: string) => {
    setInputValues((prev) => ({ ...prev, nameKana: v }))
  }, [])

  const setPatientCode = useCallback((v: string) => {
    setInputValues((prev) => ({ ...prev, patientCode: v }))
  }, [])

  const setVisitDate = useCallback(
    (date: string) => {
      updateUrl({ visit_date: date || undefined, page: undefined })
    },
    [updateUrl]
  )

  const setPage = useCallback(
    (p: number) => {
      updateUrl({ page: p === 1 ? undefined : String(p) })
    },
    [updateUrl]
  )

  const setLimit = useCallback(
    (l: number) => {
      updateUrl({
        limit: l === DEFAULT_PAGE_LIMIT ? undefined : String(l),
        page: undefined,
      })
    },
    [updateUrl]
  )

  const setSort = useCallback(
    (field: SortableField) => {
      const newOrder: SortOrder =
        sortField === field && sortOrder === 'asc' ? 'desc' : 'asc'
      const newOrdering = newOrder === 'desc' ? `-${field}` : field
      updateUrl({
        ordering:
          newOrdering === DEFAULT_SORT_FIELD && newOrder === DEFAULT_SORT_ORDER
            ? undefined
            : newOrdering,
        page: undefined,
      })
    },
    [sortField, sortOrder, updateUrl]
  )

  const setActionStatus = useCallback(
    (s: ActionStatus | undefined) => {
      updateUrl({ action_status: s, page: undefined })
    },
    [updateUrl]
  )

  const setActionGroup = useCallback(
    (g: ActionGroup | undefined) => {
      updateUrl({ action_group: g, page: undefined })
    },
    [updateUrl]
  )

  const jumpToToday = useCallback(() => {
    updateUrl({
      visit_date: dayjs().format('YYYY-MM-DD'),
      page: undefined,
    })
  }, [updateUrl])

  const clearAllFilters = useCallback(() => {
    setInputValues({ nameKanji: '', nameKana: '', patientCode: '' })
    navigate(location.pathname)
  }, [navigate, location.pathname])

  // API パラメータを構築
  const apiParams = useMemo<PatientSearchParams>(
    () => ({
      page,
      limit,
      ordering,
      ...(debouncedInput.nameKanji && {
        name_kanji: debouncedInput.nameKanji,
      }),
      ...(debouncedInput.nameKana && { name_kana: debouncedInput.nameKana }),
      ...(debouncedInput.patientCode && {
        patient_code: debouncedInput.patientCode,
      }),
      ...(visitDate && { visit_date: visitDate }),
      ...(actionStatus && { action_status: actionStatus }),
      ...(actionGroup && { action_group: actionGroup }),
    }),
    [
      page,
      limit,
      ordering,
      debouncedInput,
      visitDate,
      actionStatus,
      actionGroup,
    ]
  )

  return {
    apiParams,
    inputValues,
    visitDate,
    actionStatus,
    actionGroup,
    sortField,
    sortOrder,
    page,
    limit,
    setNameKanji,
    setNameKana,
    setPatientCode,
    setVisitDate,
    setPage,
    setLimit,
    setSort,
    setActionStatus,
    setActionGroup,
    jumpToToday,
    clearAllFilters,
  }
}
